import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  List,
  ListItem,
  ListItemText
} from '@mui/material';

const FreePlansModal = ({ userId, open, onClose }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      fetchFreePlans();
    }
  }, [open]);

  const fetchFreePlans = async () => {
    try {
      const response = await axios.get('https://landnest.net:81/subplans/');
      const freePlans = response.data.filter(plan => plan.plan_name === 'Free');
      setPlans(freePlans);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch plans:', err);
      setError('Failed to load subscription plans. Please try again later.');
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setSubmitting(true);
    setError(null);
    
    try {
      const trialDays = plans[0]?.trial_days || 10;
      const response = await axios.post('https://landnest.net:81/subscribe/free/', {
        user_id: userId,
        plan_name: 'Free',
        trial_days: trialDays
      });

      if (response.status >= 200 && response.status < 300) {
        console.log('Subscription successful:', response.data);
        onClose(true);
      } else {
        throw new Error(response.data?.message || 'Unexpected response');
      }
    } catch (err) {
      console.error('Subscription error:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.response?.data?.message || 'Failed to complete subscription');
    } finally {
      setSubmitting(false);
    }
  };

  const renderPlanFeatures = (plan) => {
    const isBuyerOrTenant = ['Buyer', 'Tenant'].includes(plan.user_type);
    const isOwnerOrAgent = ['Individual Owner/Builder', 'Landlord', 'Agent'].includes(plan.user_type);

    return (
      <Box component="div" sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {plan.user_type} Plan Features:
        </Typography>
        <List dense component="ul" sx={{ pl: 2 }}>
          <ListItem sx={{ py: 0 }}>
            <ListItemText
              primary={
                isBuyerOrTenant
                  ? `1. Owner details access limit: ${plan.no_of_properties_unlimited ? 'Unlimited' : plan.no_of_properties || '0'}`
                  : `1. Property listings limit: ${plan.no_of_properties_unlimited ? 'Unlimited' : plan.no_of_properties || '0'}`
              }
            />
          </ListItem>

          {isOwnerOrAgent && (
            <>
              <ListItem sx={{ py: 0 }}>
                <ListItemText
                  primary={`2. Buyer inquiries access limit: ${plan.matching_enquiry_unlimited ? 'Unlimited' : plan.matching_enquiry || '0'}`}
                />
              </ListItem>
              <ListItem sx={{ py: 0 }}>
                <ListItemText
                  primary={`3. Wishlist access limit: ${plan.no_of_liked_data_unlimited ? 'Unlimited' : plan.no_of_liked_data || '0'}`}
                />
              </ListItem>
            </>
          )}

          {isBuyerOrTenant ? (
            <>
              <ListItem sx={{ py: 0 }}>
                <ListItemText primary="2. Wishlist any properties" />
              </ListItem>
              <ListItem sx={{ py: 0 }}>
                <ListItemText primary="3. Chat with property owners" />
              </ListItem>
            </>
          ) : (
            <>
              <ListItem sx={{ py: 0 }}>
                <ListItemText primary="4. Property boosting feature" />
              </ListItem>
              <ListItem sx={{ py: 0 }}>
                <ListItemText primary="5. Daily wishlist tracking" />
              </ListItem>
            </>
          )}

          {/* <ListItem sx={{ py: 0 }}>
            <ListItemText primary={`Trial period: ${plan.trial_days} days`} />
          </ListItem> */}
        </List>
      </Box>
    );
  };

 return (
  <Dialog 
    open={open} 
    onClose={() => onClose(false)} 
    maxWidth="sm" 
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: '12px',
        background: 'linear-gradient(145deg, #f5f7fa 0%, #ffffff 100%)'
      }
    }}
  >
    <DialogTitle sx={{ 
      background: 'linear-gradient(90deg, #000000ff 0%, #bec88dff 100%)',
      color: 'white',
      py: 2,
      textAlign: 'center'
    }}>
      <Typography variant="h5" sx={{ 
        fontWeight: 'bold',
        fontSize: { xs: '1.25rem', sm: '1.5rem' }
      }}>
        15-Day Free Trial Plans
      </Typography>
    </DialogTitle>
    
    <DialogContent dividers sx={{ 
      py: 3,
      px: { xs: 2, sm: 3 },
      bgcolor: '#f8f9fa'
    }}>
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress size={60} thickness={4} sx={{ color: '#3f51b5' }} />
        </Box>
      ) : error ? (
        <Box sx={{
          p: 2,
          bgcolor: 'error.light',
          borderRadius: 1,
          borderLeft: '4px solid',
          borderColor: 'error.main'
        }}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </Box>
      ) : plans.length === 0 ? (
        <Typography variant="body1" align="center" sx={{ color: 'text.secondary' }}>
          No free plans currently available.
        </Typography>
      ) : (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3
        }}>
          {plans.map(plan => (
            <Paper 
              key={plan.plan_id} 
              elevation={3} 
              sx={{ 
                p: 3,
                borderRadius: '8px',
                borderLeft: '4px solid',
                borderColor: '#000000ff',
                background: 'white',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 12px rgba(0,0,0,0.1)'
                }
              }}
            >
              {renderPlanFeatures(plan)}
            </Paper>
          ))}
        </Box>
      )}
    </DialogContent>

    <DialogActions sx={{
      px: 3,
      py: 2,
      bgcolor: '#f5f7fa',
      borderTop: '1px solid #e0e0e0'
    }}>
      <Button 
        onClick={() => onClose(false)} 
        disabled={submitting}
        variant="outlined"
        sx={{
          px: 4,
          py: 1,
          borderRadius: '20px',
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px'
          }
        }}
      >
        Cancel
      </Button>
      <Button
        onClick={handleSubscribe}
        disabled={submitting || loading || plans.length === 0}
        variant="contained"
        sx={{
          px: 4,
          py: 1,
          borderRadius: '20px',
          background: 'linear-gradient(90deg, #3f51b5 0%, #2196f3 100%)',
          '&:hover': {
            background: 'linear-gradient(90deg, #303f9f 0%, #1976d2 100%)',
            boxShadow: '0 4px 8px rgba(33, 150, 243, 0.3)'
          },
          '&.Mui-disabled': {
            background: '#e0e0e0',
            color: '#9e9e9e'
          }
        }}
        startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
      >
        {submitting ? 'Processing...' : 'Continue'}
      </Button>
    </DialogActions>
  </Dialog>
);
};

export default FreePlansModal;